import AbstractFetch from '~/repositories/AbstractFetch';
import type { IFetchOptions, IResponse } from '~/types/repositories';
import type {
  IApiGetCategoriesResponse,
  IAPIGetPostDataResponse,
  IApiGetPostsResponse,
  IApiGetSectionsResponse,
} from '~/modules/infoPages/types';

/**
 * Репозиторий для работы с API информационных страниц.
 *
 * Позволяет получать секции, категории, посты и данные о конкретном посте.
 */
export default class InfoPages extends AbstractFetch {
  private static readonly SECTIONS = 'v1/sections/';
  private static readonly POSTS = 'v1/posts/';
  private static readonly CATEGORIES = 'v1/sections/{id}/categories/';
  private static readonly POST = 'v1/posts/{id}/';

  /**
   * Создаёт экземпляр InfoPages.
   * @param {IFetchOptions} fetchOptions - Опции для работы с API.
   */
  constructor(fetchOptions: IFetchOptions) {
    super(fetchOptions.baseURL, fetchOptions.fetch);
  }

  /**
   * Получает список секций по указанным путям.
   * @param {Object} [payload] - Данные для запроса.
   * @param {string[]} [payload.path] - Пути к секциям.
   * @returns {Promise<IResponse<IApiGetSectionsResponse>>} Ответ API с секциями.
   */
  async getSections(payload: { path?: string[] } = {}): Promise<IResponse<IApiGetSectionsResponse>> {
    const params = new URLSearchParams();
    if (payload.path?.length) {
      params.append('path[]', payload.path.join(','));
    }
    return this.fetch(InfoPages.SECTIONS, { queryParams: params.toString() });
  }

  /**
   * Получает категории секции.
   * @param {Object} payload - Данные для запроса.
   * @param {number} payload.id - ID секции.
   * @returns {Promise<IResponse<IApiGetCategoriesResponse>>} Ответ API с категориями.
   */
  async getSectionCategories(payload: { id: number }): Promise<IResponse<IApiGetCategoriesResponse>> {
    return this.fetch(InfoPages.CATEGORIES.replace('{id}', String(payload.id)));
  }

  /**
   * Получает список постов.
   * @param {Object} payload - Данные для запроса.
   * @param {string[]} [payload.fullPaths] - Полные пути постов.
   * @param {string[]} [payload.categories] - Категории.
   * @param {number[]} [payload.sections] - ID секций.
   * @param {number} payload.count - Количество постов.
   * @param {number} payload.page - Номер страницы.
   * @returns {Promise<IResponse<IApiGetPostsResponse>>} Ответ API с постами.
   */
  async getPosts(payload: { fullPaths?: string[]; categories?: string[]; section_id?: number; count: number; page: number; includeHidden?: boolean }): Promise<IResponse<IApiGetPostsResponse>> {
    const params = new URLSearchParams();
    if (payload.fullPaths?.length) {
      params.append('full_paths[]', payload.fullPaths.join(','));
    }
    if (payload.categories?.length) {
      params.append('categories[]', payload.categories.join(','));
    }
    if (payload.section_id) {
      params.append('section_id', payload.section_id.toString());
    }
    params.append('count', payload.count.toString());
    params.append('page', payload.page.toString());

    if (!payload.includeHidden) {
      // Запрашиваем только доступные статьи
      const isHidden = false;
      params.append('is_hidden', isHidden.toString());
    }

    return this.fetch(InfoPages.POSTS, { queryParams: params.toString() });
  }

  /**
   * Получает детальную информацию о посте.
   * @param {Object} payload - Данные для запроса.
   * @param {number} payload.id - ID поста.
   * @param {string[]} [payload.fullPaths] - Полные пути к посту (необязательно).
   * @returns {Promise<IResponse<IAPIGetPostDataResponse>>} Ответ API с данными поста.
   */
  async getPostData(payload: { id: number; fullPaths?: string[] }): Promise<IResponse<IAPIGetPostDataResponse>> {
    const params = new URLSearchParams();
    if (payload.fullPaths?.length) {
      params.append('fullPaths[]', payload.fullPaths.join(','));
    }
    return this.fetch(InfoPages.POST.replace('{id}', String(payload.id)), { queryParams: params.toString() });
  }
}
